import {
  type ISelectProps,
  IconAdd,
  PrimaryButton,
  Select,
  Space,
  TertiaryButton,
  Text,
  TextField,
} from '@procurify/ui'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardProviderOption } from './CreditCardProviderOption'
import messages from './messages'
import { useCreditCardProviderSelect } from './useCreditCardProviderSelect'

// Angular parity: the "Manage providers" sticky link navigates to the AP
// payee management page (BHV-019).
const AP_PAYEE_MANAGEMENT_URL = '/#/ap'

export interface ICreditCardProviderSelectProps extends ISelectProps<CreditCardProviderOption> {
  /**
   * Entitlement-derived (ap.everythingButExpense) and provided by the
   * consumer — the select never reads entitlements itself (BHV-019).
   */
  showManageProviders?: boolean
}

/**
 * Self-contained credit-card-provider typeahead (ADR-3 shared component, not
 * a feature). Options are type-6 vendors from
 * GET /api/v3/vendors/credit-card-providers/; a sticky "Create new" action
 * inline-creates a provider and selects it (BHV-018).
 */
export const CreditCardProviderSelect = ({
  showManageProviders = false,
  onChange,
  ...props
}: ICreditCardProviderSelectProps) => {
  const intl = useProcurifyIntl()

  const {
    data: { providers },
    states: { isFetching, isCreating, isSaving, newProviderName },
    commands: { createProvider },
    methods: { openCreate, closeCreate, setNewProviderName },
  } = useCreditCardProviderSelect({
    onCreated: (provider) =>
      onChange?.(provider, { action: 'select-option', option: provider }),
  })

  return (
    <Space size='small'>
      <Select<CreditCardProviderOption>
        options={providers}
        isLoading={isFetching}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id.toString()}
        placeholder={intl.formatMessage(messages.placeholder)}
        aria-label={intl.formatMessage(messages.ariaLabel)}
        onChange={onChange}
        stickies={({ closeMenu }) => {
          const stickies = [
            <Select.StickyOption
              key='create-new'
              onClick={() => {
                openCreate()
                closeMenu()
              }}
            >
              <Space direction='horizontal' size='xsmall' align='center'>
                <IconAdd size={12} />
                <Text bold autoMargin={false}>
                  {intl.formatMessage(messages.createNewOption)}
                </Text>
              </Space>
            </Select.StickyOption>,
          ]

          if (showManageProviders) {
            stickies.push(
              <Select.StickyOption key='manage-providers'>
                <BridgeLink to={AP_PAYEE_MANAGEMENT_URL}>
                  {intl.formatMessage(messages.manageProvidersOption)}
                </BridgeLink>
              </Select.StickyOption>
            )
          }

          return stickies
        }}
        {...props}
      />
      {isCreating && (
        <Space direction='horizontal' size='small' align='center'>
          <TextField
            id='credit-card-provider-new-name'
            inputProps={{
              'aria-label': intl.formatMessage(messages.newProviderNameLabel),
            }}
            placeholder={intl.formatMessage(
              messages.newProviderNamePlaceholder
            )}
            value={newProviderName}
            onChange={(event) => setNewProviderName(event.target.value)}
            disabled={isSaving}
          />
          <PrimaryButton
            type='button'
            onClick={() => createProvider()}
            isLoading={isSaving}
            disabled={!newProviderName.trim()}
          >
            {intl.formatMessage(messages.createButton)}
          </PrimaryButton>
          <TertiaryButton type='button' onClick={closeCreate}>
            {intl.formatMessage(messages.cancelButton)}
          </TertiaryButton>
        </Space>
      )}
    </Space>
  )
}

CreditCardProviderSelect.displayName = 'CreditCardProviderSelect'
